import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, PageQuerySchema, pageSchema } from "./common.js";

export const WorkOrderStatusSchema = z.enum([
	"draft",
	"issued",
	"implemented",
	"verified",
	"accepted",
	"rejected",
	"blocked",
]);
export type WorkOrderStatus = z.infer<typeof WorkOrderStatusSchema>;

export const WorkOrderUnblockedStatusSchema = WorkOrderStatusSchema.exclude(["blocked"]);
export type WorkOrderUnblockedStatus = z.infer<typeof WorkOrderUnblockedStatusSchema>;

export const WorkOrderTargetRepoSchema = z.enum(["zerovia", "agentry"]);
export type WorkOrderTargetRepo = z.infer<typeof WorkOrderTargetRepoSchema>;

export const WorkOrderKindSchema = z.enum(["implementation", "journey"]);
export type WorkOrderKind = z.infer<typeof WorkOrderKindSchema>;

export const WorkOrderDeployStageSchema = z.enum(["preview", "production"]);
export type WorkOrderDeployStage = z.infer<typeof WorkOrderDeployStageSchema>;

export const JourneyScriptSchema = z.enum([
	"S1_first_lp",
	"S4_morning_check",
	"S5_orderer",
	"S6_fault_injection",
]);
export type JourneyScript = z.infer<typeof JourneyScriptSchema>;

export const JourneyFaultSchema = z.enum([
	"llm_route_5xx_once",
	"runtime_auth_invalid",
	"orphan_machine",
	"clock_advance_48h",
]);
export type JourneyFault = z.infer<typeof JourneyFaultSchema>;

export const WorkOrderActorTypeSchema = z.enum([
	"human",
	"design_agent",
	"implement_agent",
	"accept_agent",
	"journey_agent",
	"system",
]);
export type WorkOrderActorType = z.infer<typeof WorkOrderActorTypeSchema>;

export const WorkOrderSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	projectId: IdSchema,
	title: z.string().min(1).max(200),
	kind: WorkOrderKindSchema,
	deployStage: WorkOrderDeployStageSchema.nullable(),
	targetRepo: WorkOrderTargetRepoSchema,
	branch: z.string().min(1).max(240),
	specMarkdown: z.string().min(1),
	journeyScript: JourneyScriptSchema.nullable(),
	scheduledAt: IsoDateTimeSchema.nullable(),
	journeyLlmCapSeconds: z.number().int().positive().nullable(),
	journeyFault: JourneyFaultSchema.nullable(),
	campaignId: z
		.string()
		.regex(/^burn_[A-Za-z0-9_-]+$/)
		.nullable(),
	campaignOrdinal: z.number().int().positive().nullable(),
	campaignSize: z.number().int().positive().nullable(),
	status: WorkOrderStatusSchema,
	blockedFromStatus: WorkOrderUnblockedStatusSchema.nullable(),
	createdBy: IdSchema,
	attentionRequired: z.boolean(),
	requiredSecrets: z.array(z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/)).default([]),
	deletedAt: IsoDateTimeSchema.nullable(),
	deletedBy: IdSchema.nullable(),
	createdAt: IsoDateTimeSchema,
	updatedAt: IsoDateTimeSchema,
});
export type WorkOrder = z.infer<typeof WorkOrderSchema>;

const WorkOrderCreateFields = {
	title: z.string().min(1).max(200),
	kind: WorkOrderKindSchema.default("implementation"),
	deployStage: WorkOrderDeployStageSchema.nullable().default(null),
	targetRepo: WorkOrderTargetRepoSchema,
	branch: z.string().min(1).max(240),
	specMarkdown: z.string().min(1),
	journeyScript: JourneyScriptSchema.nullable().default(null),
	scheduledAt: IsoDateTimeSchema.nullable().default(null),
	journeyLlmCapSeconds: z.number().int().positive().nullable().default(null),
	journeyFault: JourneyFaultSchema.nullable().default(null),
	campaignId: z
		.string()
		.regex(/^burn_[A-Za-z0-9_-]+$/)
		.nullable()
		.default(null),
	campaignOrdinal: z.number().int().positive().nullable().default(null),
	campaignSize: z.number().int().positive().nullable().default(null),
	requiredSecrets: z
		.array(z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/))
		.max(64)
		.default([]),
} as const;

export const WorkspaceSecretNameSchema = z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/);
export const WorkspaceSecretValueSchema = z.string().min(1).max(8192);
export const SetWorkspaceSecretRequestSchema = z.object({ value: WorkspaceSecretValueSchema });
export const WorkspaceSecretSchema = z.object({
	name: WorkspaceSecretNameSchema,
	fingerprint: z.string().regex(/^[0-9a-f]{12}$/),
	updatedBy: IdSchema,
	updatedAt: IsoDateTimeSchema,
});
export const WorkspaceSecretListResponseSchema = z.object({
	items: z.array(WorkspaceSecretSchema),
});
export type WorkspaceSecret = z.infer<typeof WorkspaceSecretSchema>;

export const CreateWorkOrderRequestSchema = z
	.object(WorkOrderCreateFields)
	.superRefine((value, ctx) => {
		if (value.kind === "journey" && !value.journeyScript) {
			ctx.addIssue({
				code: "custom",
				path: ["journeyScript"],
				message: "journeyScript is required for journey Work Orders",
			});
		}
		if (value.kind === "implementation" && value.journeyScript) {
			ctx.addIssue({
				code: "custom",
				path: ["journeyScript"],
				message: "journeyScript is only valid for journey Work Orders",
			});
		}
		if (value.kind !== "journey" && value.journeyFault) {
			ctx.addIssue({
				code: "custom",
				path: ["journeyFault"],
				message: "journeyFault is only valid for journey Work Orders",
			});
		}
		const campaignFields = [value.campaignId, value.campaignOrdinal, value.campaignSize];
		if (
			campaignFields.some((field) => field !== null) &&
			campaignFields.some((field) => field === null)
		) {
			ctx.addIssue({
				code: "custom",
				path: ["campaignId"],
				message: "campaign fields are all-or-nothing",
			});
		}
		if (
			value.campaignOrdinal !== null &&
			value.campaignSize !== null &&
			value.campaignOrdinal > value.campaignSize
		) {
			ctx.addIssue({
				code: "custom",
				path: ["campaignOrdinal"],
				message: "campaignOrdinal exceeds campaignSize",
			});
		}
	});
export type CreateWorkOrderRequest = z.input<typeof CreateWorkOrderRequestSchema>;

export const ImprovementArtifactRefSchema = z.object({
	id: IdSchema,
	version: z.number().int().positive(),
	hash: z.string().regex(/^[0-9a-f]{64}$/),
});

/** Internal, durable reservation created by C-01 S7. It can only materialise a draft. */
export const ReserveImprovementDraftRequestSchema = z
	.object({
		tenantId: IdSchema,
		projectId: IdSchema,
		parentExecutionId: IdSchema,
		iteration: z.number().int().min(1).max(3),
		scheduledAt: IsoDateTimeSchema,
		hypothesis: ImprovementArtifactRefSchema,
		title: z.string().min(1).max(200),
		targetRepo: WorkOrderTargetRepoSchema,
		branch: z.string().min(1).max(240),
		specMarkdown: z.string().min(1),
	})
	.strict();
export type ReserveImprovementDraftRequest = z.infer<typeof ReserveImprovementDraftRequestSchema>;

export const ImprovementDraftReservationSchema = ReserveImprovementDraftRequestSchema.extend({
	id: IdSchema,
	idempotencyKey: z.string().min(1),
	status: z.enum(["scheduled", "materialized"]),
	workOrderId: IdSchema.nullable(),
	createdAt: IsoDateTimeSchema,
});

export const UpdateWorkOrderRequestSchema = z
	.object({
		title: WorkOrderCreateFields.title.optional(),
		targetRepo: WorkOrderCreateFields.targetRepo.optional(),
		branch: WorkOrderCreateFields.branch.optional(),
		specMarkdown: WorkOrderCreateFields.specMarkdown.optional(),
		deployStage: WorkOrderCreateFields.deployStage.optional(),
	})
	.refine((value) => Object.keys(value).length > 0, "At least one field is required");
export type UpdateWorkOrderRequest = z.infer<typeof UpdateWorkOrderRequestSchema>;

export const WorkOrderTransitionRequestSchema = z.object({
	to: WorkOrderStatusSchema.exclude(["blocked"]),
	reason: z.string().min(1).max(10_000).optional(),
});
export type WorkOrderTransitionRequest = z.infer<typeof WorkOrderTransitionRequestSchema>;

export const WorkOrderListQuerySchema = PageQuerySchema.extend({
	status: WorkOrderStatusSchema.optional(),
	blocked: z
		.enum(["true", "false"])
		.transform((value) => value === "true")
		.optional(),
	includeDeleted: z
		.enum(["true", "false"])
		.transform((value) => value === "true")
		.default(false),
});
export type WorkOrderListQuery = z.infer<typeof WorkOrderListQuerySchema>;
export const WorkOrderPageSchema = pageSchema(WorkOrderSchema);
export type WorkOrderPage = z.infer<typeof WorkOrderPageSchema>;

export const WorkOrderQuestionStatusSchema = z.enum(["open", "answered", "closed"]);
export const WorkOrderQuestionSchema = z.object({
	id: IdSchema,
	orderId: IdSchema,
	seq: z.number().int().min(0),
	questionMarkdown: z.string().min(1),
	status: WorkOrderQuestionStatusSchema,
	answerMarkdown: z.string().nullable(),
	askedByActorType: WorkOrderActorTypeSchema,
	askedByActorId: IdSchema,
	answeredByActorType: WorkOrderActorTypeSchema.nullable(),
	answeredByActorId: IdSchema.nullable(),
	createdAt: IsoDateTimeSchema,
	answeredAt: IsoDateTimeSchema.nullable(),
});
export type WorkOrderQuestion = z.infer<typeof WorkOrderQuestionSchema>;
export const CreateWorkOrderQuestionRequestSchema = z.object({
	questionMarkdown: z.string().min(1).max(50_000),
});
export const AnswerWorkOrderQuestionRequestSchema = z.object({
	answerMarkdown: z.string().min(1).max(50_000),
	resume: z.boolean().optional(),
});
export const WorkOrderQuestionPageSchema = pageSchema(WorkOrderQuestionSchema);

export const RetryWorkOrderAcceptanceRequestSchema = z.object({
	reason: z.string().min(1).max(10_000),
});
export type RetryWorkOrderAcceptanceRequest = z.infer<typeof RetryWorkOrderAcceptanceRequestSchema>;

export const RetryWorkOrderAcceptanceResponseSchema = z.object({
	order: WorkOrderSchema,
	jobId: IdSchema,
	closedQuestionIds: z.array(IdSchema),
});
export type RetryWorkOrderAcceptanceResponse = z.infer<
	typeof RetryWorkOrderAcceptanceResponseSchema
>;

export const WorkOrderRecordKindSchema = z.enum([
	"詳細確認",
	"受け入れ",
	"プレビュー承認",
	"本番承認",
	"備考",
]);
export const WorkOrderRecordSchema = z.object({
	id: IdSchema,
	orderId: IdSchema,
	seq: z.number().int().min(0),
	kind: WorkOrderRecordKindSchema,
	bodyMarkdown: z.string().min(1),
	actorType: WorkOrderActorTypeSchema,
	actorId: IdSchema,
	createdAt: IsoDateTimeSchema,
});
export type WorkOrderRecord = z.infer<typeof WorkOrderRecordSchema>;
export const CreateWorkOrderRecordRequestSchema = z.object({
	kind: WorkOrderRecordKindSchema,
	bodyMarkdown: z.string().min(1).max(100_000),
});
export const WorkOrderRecordPageSchema = pageSchema(WorkOrderRecordSchema);

export const WorkOrderEvidenceKindSchema = z.enum([
	"commit",
	"ci_status",
	"release",
	"verify_run",
	"stg_check",
	"journey_result",
	/** B210 workspace pipeline evidence */
	"preview_url",
	"web_search_citation",
	"image_artifact",
	"build_log",
]);
export type WorkOrderEvidenceKind = z.infer<typeof WorkOrderEvidenceKindSchema>;
export const WorkOrderEvidenceSchema = z.object({
	id: IdSchema,
	orderId: IdSchema,
	seq: z.number().int().min(0),
	kind: WorkOrderEvidenceKindSchema,
	payload: z.record(z.string(), z.unknown()),
	actorType: WorkOrderActorTypeSchema,
	actorId: IdSchema,
	createdAt: IsoDateTimeSchema,
});
export type WorkOrderEvidence = z.infer<typeof WorkOrderEvidenceSchema>;
export const WorkOrderEvidencePageSchema = pageSchema(WorkOrderEvidenceSchema);
export const SubmitWorkOrderEvidenceRequestSchema = z.object({
	kind: WorkOrderEvidenceKindSchema,
	payload: z.record(z.string(), z.unknown()),
});
export type SubmitWorkOrderEvidenceRequest = z.infer<typeof SubmitWorkOrderEvidenceRequestSchema>;

export const WorkOrderSessionRoleSchema = z.enum(["design", "implement", "accept", "journey"]);
export type WorkOrderSessionRole = z.infer<typeof WorkOrderSessionRoleSchema>;
export const WorkOrderSessionStatusSchema = z.enum(["active", "succeeded", "failed", "abandoned"]);
export const WorkOrderSessionSchema = z.object({
	id: IdSchema,
	orderId: IdSchema,
	role: WorkOrderSessionRoleSchema,
	attempt: z.number().int().min(1),
	agentSessionId: IdSchema,
	runtimeId: IdSchema,
	status: WorkOrderSessionStatusSchema,
	framesAvailable: z.boolean().default(false),
	createdAt: IsoDateTimeSchema,
	endedAt: IsoDateTimeSchema.nullable(),
});
export type WorkOrderSession = z.infer<typeof WorkOrderSessionSchema>;
export const StartWorkOrderSessionRequestSchema = z.object({
	role: z.literal("design"),
});
export const WorkOrderSessionPageSchema = pageSchema(WorkOrderSessionSchema);

export const ConversationFrameEventSchema = z.enum([
	"exec",
	"stdout",
	"stderr",
	"error",
	"exit",
	"truncated",
	"orders_created",
]);
export type ConversationFrameEvent = z.infer<typeof ConversationFrameEventSchema>;

export const OrdersCreatedFrameDataSchema = z.object({
	orderIds: z.array(IdSchema).min(1),
});

export const ConversationFrameSchema = z
	.object({
		sequence: z.number().int().min(0),
		event: ConversationFrameEventSchema,
		data: z.unknown(),
		createdAt: IsoDateTimeSchema,
	})
	.superRefine((frame, ctx) => {
		if (frame.event === "orders_created") {
			const parsed = OrdersCreatedFrameDataSchema.safeParse(frame.data);
			if (!parsed.success) {
				ctx.addIssue({
					code: "custom",
					message: "orders_created data must contain typed orderIds",
					path: ["data"],
				});
			}
		}
	});
export type ConversationFrame = z.infer<typeof ConversationFrameSchema>;

export const ConversationFramesSnapshotSchema = z.object({
	items: z.array(ConversationFrameSchema),
	lastSequence: z.number().int().min(-1),
	complete: z.boolean(),
});
export type ConversationFramesSnapshot = z.infer<typeof ConversationFramesSnapshotSchema>;

export const ConversationInputRequestSchema = z.object({
	message: z.string().min(1).max(32_000),
});
export type ConversationInputRequest = z.infer<typeof ConversationInputRequestSchema>;

export const DesignSessionStatusSchema = z.enum(["active", "succeeded", "failed"]);
export const DesignSessionSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	projectId: IdSchema,
	ownerUserId: IdSchema,
	cli: z.enum(["claude", "codex"]),
	status: DesignSessionStatusSchema,
	createdAt: IsoDateTimeSchema,
	updatedAt: IsoDateTimeSchema,
	endedAt: IsoDateTimeSchema.nullable(),
});
export type DesignSession = z.infer<typeof DesignSessionSchema>;

export const StartDesignSessionRequestSchema = z.object({
	cli: z.enum(["claude", "codex"]).default("claude"),
	message: z.string().min(1).max(32_000),
});
export type StartDesignSessionRequest = z.infer<typeof StartDesignSessionRequestSchema>;

export const WorkOrderProgressPhaseSchema = z.enum([
	"queued",
	"preparing",
	"executing",
	"verifying",
	"finalizing",
	"integrating",
	"retry_wait",
	"succeeded",
	"failed",
	"canceled",
]);
export type WorkOrderProgressPhase = z.infer<typeof WorkOrderProgressPhaseSchema>;

export const WorkOrderWallClockSchema = z.object({
	totalSeconds: z.number().nonnegative(),
	queuedSeconds: z.number().nonnegative(),
	wakeSeconds: z.number().nonnegative(),
	prepareSeconds: z.number().nonnegative(),
	llmSeconds: z.number().nonnegative(),
	ciSeconds: z.number().nonnegative(),
	integrateSeconds: z.number().nonnegative(),
	deploySeconds: z.number().nonnegative(),
	evidenceWaitSeconds: z.number().nonnegative(),
	acceptSeconds: z.number().nonnegative(),
});
export type WorkOrderWallClock = z.infer<typeof WorkOrderWallClockSchema>;

export const WorkOrderJourneySummarySchema = z.object({
	script: JourneyScriptSchema,
	result: z.enum(["scheduled", "running", "passed", "failed", "skipped"]),
	scheduledAt: IsoDateTimeSchema.nullable(),
	startedAt: IsoDateTimeSchema.nullable(),
	completedAt: IsoDateTimeSchema.nullable(),
	durationSeconds: z.number().nonnegative(),
	failedStep: z.string().nullable(),
	screenshotPaths: z.array(z.string()),
	evaluationScore: z.number().nonnegative().nullable(),
	evaluationFailCount: z.number().int().nonnegative().nullable(),
	llmSeconds: z.number().nonnegative(),
	flySeconds: z.number().nonnegative(),
});
export type WorkOrderJourneySummary = z.infer<typeof WorkOrderJourneySummarySchema>;

export const WorkOrderProgressSchema = z.object({
	orderId: IdSchema,
	sequence: z.number().int().min(0),
	phase: WorkOrderProgressPhaseSchema,
	jobKind: WorkOrderSessionRoleSchema.nullable(),
	attempt: z.number().int().min(1).nullable(),
	startedAt: IsoDateTimeSchema.nullable(),
	retryCount: z.number().int().min(0),
	retryReason: z.string().nullable(),
	nextRetryAt: IsoDateTimeSchema.nullable(),
	failureClass: z.enum(["transient", "deterministic"]).nullable(),
	warning: z.string().nullable(),
	journey: WorkOrderJourneySummarySchema.nullable(),
	wallClock: WorkOrderWallClockSchema,
	createdAt: IsoDateTimeSchema,
});
export type WorkOrderProgress = z.infer<typeof WorkOrderProgressSchema>;

/** Project 単位の Work Order 実行設定（Orders Block が所有）。runtimeParallelism = 1 で直列、2 以上で並行 */
export const WorkOrderProjectSettingsSchema = z.object({
	runtimeParallelism: z.number().int().min(1).max(4),
	updatedBy: IdSchema.nullable(),
	updatedAt: IsoDateTimeSchema.nullable(),
});
export type WorkOrderProjectSettings = z.infer<typeof WorkOrderProjectSettingsSchema>;

export const UpdateWorkOrderProjectSettingsRequestSchema = z.object({
	runtimeParallelism: z.number().int().min(1).max(4),
	reason: z.string().min(1).max(500).optional(),
});
export type UpdateWorkOrderProjectSettingsRequest = z.infer<
	typeof UpdateWorkOrderProjectSettingsRequestSchema
>;
